import React, { useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  formatImpactComparisonValue,
  getImpactCategoryValue,
} from "./impactValue";
import "./LcaComparisonDialog.css";

const SLOT_COUNT = 5;

/**
 * LCA Sammenligning dialog — up to 5 selected rows across impact categories.
 */
const LcaComparisonDialog = ({
  open,
  onClose,
  title = "LCA Sammenligning",
  impactCategories = [],
  selectedIds = [],
  items = [],
  getItemId = (item) => item?.id,
  getItemLabel = (item) =>
    item?.component ||
    item?.description ||
    item?.nickname ||
    item?.name ||
    `Item ${item?.id ?? ""}`,
  getValue = (item, category) => getImpactCategoryValue(item, category?.key),
  maxSelected = SLOT_COUNT,
}) => {
  const selectedItems = useMemo(() => {
    return selectedIds
      .map((id) =>
        items.find((item) => String(getItemId(item)) === String(id))
      )
      .filter(Boolean);
  }, [selectedIds, items, getItemId]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      className="lca-comparison-dialog"
      PaperProps={{ className: "lca-comparison-dialog__paper" }}
    >
      <DialogTitle className="lca-comparison-dialog__title">
        <div className="lca-comparison-dialog__title-text">
          <span className="lca-comparison-dialog__heading">{title}</span>
          <span className="lca-comparison-dialog__subtitle">
            {selectedItems.length} af {maxSelected} elementer
            {impactCategories.length > 1
              ? ` på tværs af ${impactCategories.length} påvirkningskategorier`
              : ""}
          </span>
        </div>
        <IconButton
          aria-label="Close comparison"
          onClick={onClose}
          className="lca-comparison-dialog__close"
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent className="lca-comparison-dialog__content" dividers>
        <div className="lca-comparison-dialog__grid-wrap">
          <div
            className="lca-comparison-dialog__header-row"
            style={{
              gridTemplateColumns: `160px 60px repeat(${SLOT_COUNT}, minmax(70px, 1fr))`,
            }}
          >
            <div className="lca-comparison-dialog__col-label">Kategori</div>
            <div className="lca-comparison-dialog__col-label lca-comparison-dialog__col-label--center">
              Enhed
            </div>
            {Array.from({ length: SLOT_COUNT }).map((_, slotIndex) => {
              const item = selectedItems[slotIndex];
              return (
                <div
                  key={slotIndex}
                  className="lca-comparison-dialog__col-label lca-comparison-dialog__col-label--right"
                  title={item ? getItemLabel(item) : undefined}
                >
                  {item ? getItemLabel(item) : `Slot ${slotIndex + 1}`}
                </div>
              );
            })}
          </div>

          {impactCategories.map((category, catIndex) => {
            const values = Array.from({ length: SLOT_COUNT }).map(
              (_, slotIndex) => {
                const item = selectedItems[slotIndex];
                return item ? getValue(item, category) : null;
              }
            );
            const validValues = values.filter(
              (v) => v != null && Number.isFinite(Number(v))
            );
            const maxValue =
              validValues.length > 0 ? Math.max(...validValues.map(Number)) : 0;

            return (
              <div
                key={category.key || catIndex}
                className="lca-comparison-dialog__data-row"
                style={{
                  gridTemplateColumns: `160px 60px repeat(${SLOT_COUNT}, minmax(70px, 1fr))`,
                }}
              >
                <div
                  className="lca-comparison-dialog__category"
                  title={category.name}
                >
                  {category.name}
                </div>
                <div className="lca-comparison-dialog__unit">
                  {category.unit}
                </div>
                {Array.from({ length: SLOT_COUNT }).map((_, slotIndex) => {
                  const item = selectedItems[slotIndex];
                  const value = item
                    ? formatImpactComparisonValue(getValue(item, category))
                    : "-";
                  const numValue =
                    value !== "-" ? parseFloat(value) : null;
                  const isMax =
                    numValue != null &&
                    numValue === maxValue &&
                    maxValue > 0;

                  return (
                    <div
                      key={slotIndex}
                      className={`lca-comparison-dialog__value${
                        isMax ? " lca-comparison-dialog__value--max" : ""
                      }`}
                    >
                      <span>{value}</span>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LcaComparisonDialog;
